<template>
  <div class="pipe-network-map">
    <div id="main-map" class="map-container"></div>
    
    <!-- 信息弹窗 -->
    <div id="monitor-popup" v-show="visible" class="monitor-popup">
      <div class="popup-header">
        <span>{{ clickCurrentRow.name || $t('详情') }}</span>
        <i class="icon-close" @click="closePopup">×</i>
      </div>
      <div class="popup-content">
        <div class="info-item">
          <span class="label">{{ $t('编码:') }}</span>
          <span class="value">{{ clickCurrentRow.code }}</span>
        </div>
        <div class="info-item">
          <span class="label">{{ $t('名称:') }}</span>
          <span class="value">{{ clickCurrentRow.name }}</span>
        </div>
        <div class="info-item" v-if="clickCurrentRow.type">
          <span class="label">{{ $t('类型:') }}</span>
          <span class="value">{{ formatType(clickCurrentRow.type) }}</span>
        </div>
        <div class="info-item" v-if="clickCurrentRow.height">
          <span class="label">{{ $t('高程:') }}</span>
          <span class="value">{{ clickCurrentRow.height }}</span>
        </div>
        <template v-if="clickCurrentRow.type === 'NODE_PIPELINE_TYPE_PIPELINE'">
          <div class="info-item">
            <span class="label">{{ $t('材质:') }}</span>
            <span class="value">{{ clickCurrentRow.materialType || clickCurrentRow.material_type }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('管径:') }}</span>
            <span class="value">{{ clickCurrentRow.diameter }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('管长:') }}</span>
            <span class="value">{{ clickCurrentRow.length }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Overlay from 'ol/Overlay.js'
import 'ol/ol.css'
import Map from 'ol/Map'
import View from 'ol/View'
import { get as getProjection, fromLonLat, toLonLat } from 'ol/proj'
import { createLayerEm_topology_node_pipeline } from '@/views/DMAPartitionMetering/PipelineNetMap/module/layers/Em_topology_node_pipeline.js'
import { createLayerEM_MAP_ALL_18 } from '@/views/DMAPartitionMetering/PipelineNetMap/module/layers/EM_MAP_ALL_18_DARK.js'
import { createLayerTp_node } from '@/views/DMAPartitionMetering/PipelineNetMap/module/layers/Tp_node.js'
import { createLayerTp_pipeline } from '@/views/DMAPartitionMetering/PipelineNetMap/module/layers/Tp_pipeline.js'
import { createLayerTp_area } from '@/views/DMAPartitionMetering/PipelineNetMap/module/layers/Tp_area.js'
import { createGetFeatureInfo } from '@/views/DMAPartitionMetering/PipelineNetMap/module/comFunction/WmsGetFeatureInfo.js'
import { parseHexWKB, zoomToPoint, zoomToLine } from '@/views/DMAPartitionMetering/PipelineNetMap/module/comFunction/ComFunction.js'
import { updateOrCreateGeoServerStyle } from '@/views/DMAPartitionMetering/PipelineNetMap/module/comFunction/SLDFunction.js'
import { Vector as VectorLayer } from 'ol/layer'
import { Vector as VectorSource } from 'ol/source'
import { vectorStyle } from '@/views/DMAPartitionMetering/PipelineNetMap/module/style/VectorStyle.js'
import { Feature } from 'ol'
import CONFIG from '@/views/DMAPartitionMetering/PipelineNetMap/module/config/GIS_Config'
import { filterDictTextByCache } from '@/components/dict/JDictSelectUtil'
import { updateTopology } from '@/views/DMAPartitionMetering/PipelineNetMap/module/config/GIS_Config'
import Point from 'ol/geom/Point'

export default {
  name: 'PipeNetworkMap',
  data() {
    return {
      filterDictTextByCache: filterDictTextByCache,
      map: {},
      tileLayer: {},
      baseLayer: {},
      nodeLayerNode: {},
      pipeLineLayer: {},
      areaLayer: {},
      highlightLayer: {},
      tileSource: {},
      view: {},
      resolutions: [],
      matrixIds: [],
      projection: '',
      clickCurrentRow: {
        project_code: '',
        topology_code: '',
        code: '',
        name: '',
        type: '',
      },
      visible: false,
      zoomLevel: 0,
      topology: '',
      popup: null
    }
  },
  mounted() {
    updateTopology()
    this.$nextTick(async () => {
      this.initMap()
      this.map.on('click', this.handleMapClick)
      this.initHighlightLayer()
      this.map.addLayer(this.highlightLayer)
      
      // 初始化详情页Overlay
      this.popup = new Overlay({
        element: document.getElementById('monitor-popup'),
        className: 'custom-popup',
      })
      this.map.addOverlay(this.popup)
    })
  },
  beforeDestroy() {
    if (this.map) {
      this.map.dispose()
    }
  },
  methods: {
    initMap() {
      // 1. 投影配置
      this.projection = getProjection(CONFIG.PROJECTION)
      let wmtsLayerExtent = CONFIG.DEFAULT_EXTENT

      // 图层组
      this.tileLayer = createLayerEm_topology_node_pipeline()
      this.tileSource = this.tileLayer.getSource()
      
      // 底图
      this.baseLayer = createLayerEM_MAP_ALL_18()
      
      // 点图层
      this.nodeLayerNode = createLayerTp_node()
      
      // 线图层
      this.pipeLineLayer = createLayerTp_pipeline()
      
      // 区域图层
      this.areaLayer = createLayerTp_area()
      
      this.updateLayerParams(true)

      // 2. 动态计算中心点
      const center = [
        (wmtsLayerExtent[0] + wmtsLayerExtent[2]) / 2,
        (wmtsLayerExtent[1] + wmtsLayerExtent[3]) / 2,
      ]

      // 3. 转换为投影坐标系
      const projectedCenter = fromLonLat(center)
      const projectedExtent = [
        ...fromLonLat([wmtsLayerExtent[0], wmtsLayerExtent[1]]),
        ...fromLonLat([wmtsLayerExtent[2], wmtsLayerExtent[3]]),
      ].flat()

      // 4. 创建视图
      this.view = new View({
        center: projectedCenter,
        zoom: 11,
        projection: this.projection,
      })

      // 7. 创建地图实例
      this.map = new Map({
        target: 'main-map',
        layers: [this.baseLayer, this.pipeLineLayer, this.nodeLayerNode],
        view: this.view,
        controls: [] // 大屏不需要控制面板
      })

      // 设置初始范围
      this.$nextTick(() => {
        this.map.getView().fit(projectedExtent, {
          padding: [20, 20, 20, 20],
          duration: 0,
        })
      })
    },

    initHighlightLayer() {
      this.highlightLayer = new VectorLayer({
        source: new VectorSource(),
        style: feature => vectorStyle(feature),
        zIndex: 9999,
      })
    },

    async handleMapClick(evt) {
      const coordinate = evt.coordinate
      const view = this.map.getView()
      const resolution = view.getResolution()
      const projection = view.getProjection()

      try {
        const layers = [
          { layer: this.nodeLayerNode, name: 'tp_node_sql' },
          { layer: this.pipeLineLayer, name: 'tp_pipeline_sql' },
        ]
        
        const results = await Promise.all(
          layers.map(async layerInfo => {
            const info = await createGetFeatureInfo({
              map: this.map,
              coordinate,
              layer: layerInfo.layer,
              layerName: layerInfo.name,
              resolution,
              projection,
            })
            return { ...layerInfo, info }
          })
        )

        if (results[0].info.features.length > 0) {
          this.clickCurrentRow = Object.assign({}, results[0].info.features[0].properties, {
            id: results[0].info.features[0].id,
          })
          let item = await this.getNodeInfo(this.clickCurrentRow.code)
          this.parseAndHighLight(item)
          this.popup.setPosition(coordinate)
          this.visible = true
        } else if (results[1].info.features.length > 0) {
          this.clickCurrentRow = Object.assign({}, results[1].info.features[0].properties, {
            id: results[1].info.features[0].id,
          })
          let item = await this.getLineInfo(this.clickCurrentRow.code)
          this.parseAndHighLight(item)
          this.popup.setPosition(coordinate)
          this.visible = true
        } else {
          this.popup.setPosition(undefined)
          this.visible = false
          const highlightSource = this.highlightLayer.getSource()
          highlightSource.clear()
        }
      } catch (error) {
        console.error('WMS查询失败:', error)
      }
    },

    async getNodeInfo(code) {
      if (code == null) return null
      let parm = {
        projectCode: CONFIG.TopologyProjection,
        topologyCode: CONFIG.TopologyCode,
        code: code,
        name: null,
      }
      try {
        const res = await import('@/views/DMAPartitionMetering/PipelineNetMap/module/comFunction/ApiFunction.js')
          .then(module => module.getPostGreSqlNodeInfoByCode(parm))
        if (res.success) {
          return res.result
        } else {
          return null
        }
      } catch (error) {
        console.error('获取节点信息失败:', error)
        return null
      }
    },

    async getLineInfo(code) {
      if (code == null) return null
      let parm = {
        projectCode: CONFIG.TopologyProjection,
        topologyCode: CONFIG.TopologyCode,
        code: code,
        name: null,
      }
      try {
        const res = await import('@/views/DMAPartitionMetering/PipelineNetMap/module/comFunction/ApiFunction.js')
          .then(module => module.getPostGreSqlLineInfoByCode(parm))
        if (res.success) {
          return res.result
        } else {
          return null
        }
      } catch (error) {
        console.error('获取管线信息失败:', error)
        return null
      }
    },

    parseAndHighLight(item) {
      if (item == null) return
      const { parseHexWKB } = require('@/views/DMAPartitionMetering/PipelineNetMap/module/comFunction/ComFunction.js')
      const geometry = parseHexWKB(item.geometry, 'EPSG:3857', 'EPSG:3857')
      if (!geometry) return

      const highlightSource = this.highlightLayer.getSource()
      highlightSource.clear()

      const feature = new Feature({
        geometry: geometry,
        featureType: item.type?.startsWith('NODE_PIPELINE_TYPE_VALVE') ? 'line' : 'point',
      })
      highlightSource.addFeature(feature)
      return geometry
    },

    formatType(type) {
      return filterDictTextByCache('node_type', type) || type
    },

    closePopup() {
      this.popup.setPosition(undefined)
      this.visible = false
      const highlightSource = this.highlightLayer.getSource()
      highlightSource.clear()
    },

    updateLayerParams(tickTime) {
      const layers = [this.nodeLayerNode, this.pipeLineLayer, this.areaLayer]
      layers.forEach(layer => {
        if (layer && layer.getSource && layer.getSource().updateParams) {
          layer.getSource().updateParams({
            VIEWPARAMS: `projectCode:${CONFIG.TopologyProjection};topologyCode:${CONFIG.TopologyCode}`,
            t: Date.now(),
          })
          layer.getSource().refresh()
        }
      })
    }
  }
}
</script>

<style scoped>
.pipe-network-map {
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.monitor-popup {
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(0, 255, 200, 0.5);
  border-radius: 8px;
  padding: 15px;
  min-width: 200px;
  z-index: 200;
  box-shadow: 0 0 20px rgba(0, 255, 200, 0.3);
  filter: contrast(175%) brightness(103%);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 255, 200, 0.3);
}

.popup-header span {
  color: #00ffc8;
  font-weight: bold;
  font-size: 14px;
}

.icon-close {
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
}

.icon-close:hover {
  color: #ffaa00;
}

.popup-content .info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 12px;
}

.popup-content .label {
  color: rgba(255, 255, 255, 0.7);
}

.popup-content .value {
  color: #00ffc8;
  font-weight: bold;
}
</style>